import React from 'react'
import { Button, Checkbox, Divider, Popover, Tooltip } from 'antd'
import { SettingOutlined, HolderOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import SortableList, { SortableItem as EasySortItem, SortableKnob } from 'react-easy-sort'
import arrayMove from 'array-move'

import IntlMessages from '../../../../util/IntlMessages'
import { SegmentedToggle } from '../../../../design-system/SegmentedToggle'

interface IColumnSettingOption {
  id: string
  label: React.ReactNode
}

export interface IColumnSettingsPopoverProps {
  options: IColumnSettingOption[]
  columnOrder: string[]
  columnVisibility: Record<string, boolean>
  setColumnOrder: (order: string[]) => void
  setColumnHidden: (columnId: string, hidden: boolean) => void
  showAll: () => void
  resetToDefault: () => void
  sortable?: boolean
  leaveUnit?: 'days' | 'hours'
  onLeaveUnitChange?: (unit: 'days' | 'hours') => void
  density?: { value: 'comfortable' | 'compact'; onChange: (density: 'comfortable' | 'compact') => void }
  seeBalances?: { value: boolean; onChange: (value: boolean) => void }
}

export const ColumnSettingsPopover = (props: IColumnSettingsPopoverProps): React.ReactElement => {
  const {
    options,
    columnOrder,
    columnVisibility,
    setColumnOrder,
    setColumnHidden,
    showAll,
    resetToDefault,
    sortable = true,
    leaveUnit,
    onLeaveUnitChange,
    density,
    seeBalances,
  } = props
  const { formatMessage } = useIntl()

  // Three stacked toggles would push the column list below the fold, so they shrink once there is
  // more than the leave unit up there.
  const dense = Boolean(density || seeBalances)
  const toggleSize = dense ? 'mini' : 'medium'
  const dividerMargin = dense ? '4px 0' : '8px 0'

  const orderedOptions = [...options].sort((a, b) => columnOrder.indexOf(a.id) - columnOrder.indexOf(b.id))

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    setColumnOrder(
      arrayMove(
        orderedOptions.map((option) => option.id),
        oldIndex,
        newIndex
      )
    )
  }

  const checkboxFor = (option: IColumnSettingOption) => (
    <Checkbox checked={columnVisibility[option.id] !== false} onChange={(event) => setColumnHidden(option.id, !event.target.checked)}>
      {option.label}
    </Checkbox>
  )

  const content = (
    <div className="vt-users-column-settings">
      {leaveUnit && onLeaveUnitChange && (
        <>
          <SegmentedToggle
            size={toggleSize}
            label={formatMessage({ id: 'users.table.leaveUnitLabel' })}
            options={[
              { label: formatMessage({ id: 'components.tables.durationDays' }), value: 'days' },
              { label: formatMessage({ id: 'components.tables.durationHours' }), value: 'hours' },
            ]}
            value={leaveUnit}
            onChange={(value) => onLeaveUnitChange(value as 'days' | 'hours')}
          />
          <Divider style={{ margin: dividerMargin }} />
        </>
      )}
      {density && (
        <>
          <SegmentedToggle
            size={toggleSize}
            label={formatMessage({ id: 'users.columns.densityLabel' })}
            options={[
              { label: formatMessage({ id: 'users.columns.densityCompact' }), value: 'compact' },
              { label: formatMessage({ id: 'users.columns.densityComfortable' }), value: 'comfortable' },
            ]}
            value={density.value}
            onChange={(value) => density.onChange(value as 'comfortable' | 'compact')}
          />
          <Divider style={{ margin: dividerMargin }} />
        </>
      )}
      {seeBalances && (
        <>
          <SegmentedToggle
            size={toggleSize}
            label={formatMessage({ id: 'users.columns.seeBalances' })}
            options={[
              { label: formatMessage({ id: 'app.yes' }), value: true },
              { label: formatMessage({ id: 'app.no' }), value: false },
            ]}
            value={seeBalances.value}
            onChange={(value) => seeBalances.onChange(value === true)}
          />
          <Divider style={{ margin: dividerMargin }} />
        </>
      )}
      <div className="vt-users-column-settings-footer">
        <Button type="link" size="small" onClick={showAll}>
          <IntlMessages id="users.table.showAll" />
        </Button>
        <Button type="link" size="small" onClick={resetToDefault}>
          <IntlMessages id="users.table.resetToDefault" />
        </Button>
      </div>
      {sortable ? (
        <SortableList onSortEnd={onSortEnd} className="sortable-column-list" draggedItemClassName="dragged-column-item">
          {orderedOptions.map((option) => (
            <EasySortItem key={option.id}>
              <div className="vt-users-column-settings-item">
                <SortableKnob>
                  <span className="vt-users-column-settings-knob">
                    <HolderOutlined />
                  </span>
                </SortableKnob>
                {checkboxFor(option)}
              </div>
            </EasySortItem>
          ))}
        </SortableList>
      ) : (
        <div className="sortable-column-list">
          {orderedOptions.map((option) => (
            <div className="vt-users-column-settings-item" key={option.id}>
              {checkboxFor(option)}
            </div>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <Popover content={content} trigger="click" placement="bottomRight">
      <Tooltip title={<IntlMessages id="users.table.editColumns" />}>
        <Button icon={<SettingOutlined />} aria-label={formatMessage({ id: 'users.table.editColumns' })} />
      </Tooltip>
    </Popover>
  )
}
